import React, { useEffect, useState } from 'react'

import Transition from '@material-ui/core/Collapse'
import FormControl from '@material-ui/core/FormControl'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { cloneDeep, isEqual } from 'lodash'
import { useSelector } from 'react-redux'

import type { RootState } from 'reduxStore'

import * as Model from 'api/model'
import PoziAPISearch from 'api/search'
import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import { layerIsValidVectorLayer } from 'utils/MapUtils'
import { layerValidInCurrentProfile } from 'utils/profile'
import { sanitiseHtml } from 'utils/url'

import SearchAdvancedButton from './SearchAdvancedButton'
import SearchAutoComplete, { useMountRef } from './SearchAutoComplete'
import SearchLayers from './SearchLayers'

const useStyles = makeStyles({
  caption: {
    color: 'grey',
    display: 'flex',
    flex: 1,
    fontSize: 14,
    padding: 10,
    paddingBottom: 5
  },
  searchForm: {
    paddingBottom: 10
  }
})

// Some defaults for the caption text.
const getLayerCaption = (layerConf: ConfigLayerOptions) => {
  if (!layerConf) {
    return ''
  }

  // Some predefined layer captions
  let caption:string = layerConf.search?.caption || ''
  if (caption === '') {
    switch (layerConf.title?.toLowerCase()) {
      case 'Address'.toLowerCase():
        caption = 'Start typing an address and pick from the results'
        break
      case 'Property Number'.toLowerCase():
        caption = 'Council Property Number'
        break
      case 'Parcel SPI'.toLowerCase():
      case 'Parcel SPI Number'.toLowerCase():
        caption = 'Vicmap Standard Parcel Identifier (eg 1\\PS407741)'
        break
      case 'Place Name'.toLowerCase():
        caption = 'Vicmap Features of Interest'
        break
      case 'Property PFI'.toLowerCase():
        caption = 'Vicmap Property Persistent Feature Identifier'
        break
      default:
        caption = ''
    }
  }

  return caption
}

/**
 * Uses pipe character to split the searchField setting into a searchFieldName, searchTitle and isSearchWhatsHere.
 * @param searchField - The string value from the layerConf.searchFields setting
 * @returns [searchFieldName, searchTitle, isSearchWhatsHere]
 * searchFieldName is the name of the field in the feature properties to search.
 * searchTitle is the title of the search field to display in the search options.
 * isSearchWhatsHere is a string indicating whether the search should return a What's Here result or just a regular feature result.
 */
const parseSearchField = (searchField: string): [string, string | undefined, string] => {
  const searchFieldParts = searchField.split('|')
  // console.debug('SearchOptions - searchFieldParts', searchFieldParts)

  const searchFieldName = searchFieldParts[0].trim()
  const searchTitle = searchFieldParts?.[1]?.trim()
  const isSearchWhatsHere = searchFieldParts?.[2]?.trim() === 'true' ? 'true' : 'false'

  return [searchFieldName, searchTitle, isSearchWhatsHere]
}

/**
 * This function takes a layerConf and returns a clone of the layerConf with
 * the search options added to it. The search options are either the ones
 * hardcoded in the layerConf (search.enabled) or the ones that are
 * dynamically generated for vector layers.
 *
 **/
const convertLayerConfToSearchOptions = (layerConf: ConfigLayerOptions) => {
  const searchOptions: ConfigLayerOptions[] = []
  const isHardcodedSearchLayer = layerConf?.search?.enabled

  if (isHardcodedSearchLayer || (layerIsValidVectorLayer(layerConf) && layerConf?.showInLayerControl)) {
    const searchOption = cloneDeep(layerConf)

    searchOption.search = {
      ...searchOption.search,

      group: isHardcodedSearchLayer ? 'search' : 'All searchable layers',
      description: searchOption.title
    }

    searchOptions.push(searchOption)
  } else if (Array.isArray(layerConf.searchFields)) {
    layerConf.searchFields.forEach((searchField) => {
      const [searchFieldName, searchTitle, isSearchWhatsHere] = parseSearchField(searchField)
      const displayName = layerConf?.attributes?.[searchFieldName]?.alias || searchFieldName
      const searchOption = cloneDeep(layerConf)

      searchOption.search = {
        ...searchOption.search,

        type: 'pozisearchfields',
        group: 'search',
        description: searchTitle ?? `${searchOption.title} (${displayName})`,
        field: searchFieldName,
        whatsHere: isSearchWhatsHere
      }
      searchOptions.push(searchOption)
    })
  }

  return searchOptions
}

const SearchOptions = () => {
  const classes = useStyles()

  // Get selected option from selected layer in the model
  const currentSearchLayer = Model.get('app.search.layer', null) as ConfigLayerOptions

  // State management
  const [searchOptions, setSearchOptions] = useState([] as ConfigLayerOptions[])
  const [searchLayer, setSelectedOption] = useState(currentSearchLayer)

  // Get Profile information from redux store
  const currentProfile: string = useSelector((state: RootState) => state.currentProfile)
  const availableProfiles: Record<string, any> = useSelector((state: RootState) => state.profiles)

  // Keep track of whether or not component is mounted.
  // This is to prevent memory leaks when async functions
  // try to access the component after it has been unmounted
  const mountRef = useMountRef()

  // Update selected option when changed outside this component
  Model.on('app.search.layer', (layerConf: ConfigLayerOptions) => {
    if (mountRef.current && !isEqual(layerConf, searchLayer)) {
      setSelectedOption(layerConf)
    }
  })

  // First run (i.e. component did mount)
  useEffect(() => {
    console.debug('SearchOptions - init', mountRef.current)

    generateSearchOptions()
  }, []) // Run once

  // Change in search options
  useEffect(() => {
    console.debug('SearchOptions - change', mountRef.current, searchLayer)

    // Select first option if not selected yet
    if (!searchLayer) {
      const firstSearchOption = searchOptions?.[0]
      if (firstSearchOption) {
        console.debug('SearchOptions - select first option', firstSearchOption)
        setSelectedOption(firstSearchOption)
      } else {
        console.log('SearchOptions - unable to select first option', searchOptions)
      }
    }
  }, [searchOptions])

  // Store layerConf for selected option
  useEffect(() => {
    console.debug('SearchOptions - value change', searchLayer)

    // Prevent memory leaks
    if (!mountRef.current) {
      return
    }

    if (!searchOptions || searchOptions.length === 0) {
      return
    }

    if (!searchLayer) {
      return
    }

    const titleid = searchLayer?.titleid || 'unknown_layer'

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: titleid,
      action: 'Search Option Change'
    })

    PoziAPISearch().setSearchLayer(searchLayer)
  }, [searchLayer])

  const layerIsAvailableToCurrentUser = (layerConf: ConfigLayerOptions) => {
    const { titleid, localDataSource } = layerConf

    const localDataSourceDetected = Model.get('app.LocalDataSourceDetected', false)
    if (!localDataSourceDetected && localDataSource === true) {
      // console.debug('SearchOptions - local datasource required but not detected. Skipping.', titleid)

      return false
    }

    // Check if the layer is valid in the current profile.
    const layerIsValidInCurrentProfile = layerValidInCurrentProfile(titleid)
    if (!layerIsValidInCurrentProfile) {
      // console.debug('SearchOptions - layer not valid in current profile. Skipping.', titleid)

      return false
    }

    return true
  }

  const generateSearchOptions = () => {
    // console.debug('SearchOptions - generateSearchOptions')

    const layerConfs: ConfigLayerOptions[] = Model.get('config.layers', [])
    const searchLayersHardcoded: ConfigLayerOptions[] = []
    const searchLayersKeyword: ConfigLayerOptions[] = []
    const searchLayersVectorActive: ConfigLayerOptions[] = []
    const searchLayersVectorAll: ConfigLayerOptions[] = []

    // Add hardcoded search layers first without sorting layers alphabetically by title
    layerConfs.forEach((layerConf) => {
      if (!layerIsAvailableToCurrentUser(layerConf)) {
        return
      }

      // Create a clone of the layer config and add relevant information to enable search
      const layerSearchOptions = convertLayerConfToSearchOptions(layerConf)

      // Old fashioned search layers (like HERE search)
      if (layerConf.search?.enabled) {
        searchLayersHardcoded.push(...layerSearchOptions)
        return
      }

      // Searchable datasets that are not exposed as a layer. E.g. a Property layer
      // that is available in Whats Here.
      // The `searchFields` config option is usually provided as a keyword.
      if (layerConf.searchFields && layerIsValidVectorLayer(layerConf)) {
        searchLayersKeyword.push(...layerSearchOptions)
      }
    })

    // For all other searchable layers, sort alphabetically by title
    // Create a deep copy to avoid mutating the original array in the Model
    const sortedLayerConfs = cloneDeep(layerConfs).sort((a, b) => a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1)

    sortedLayerConfs.forEach((layerConf) => {
      if (!layerIsAvailableToCurrentUser(layerConf)) {
        return
      }

      // Skip hardcoded search layers and `searchFields` searches as they have already been added
      if (layerConf.search?.enabled || layerConf.searchFields) {
        return
      }

      // Create a clone of the layer config and add relevant information to enable search
      const layerSearchOptions = convertLayerConfToSearchOptions(layerConf)

      // Searchable vector layers
      if (layerConf?.showInLayerControl && layerIsValidVectorLayer(layerConf)) {
        searchLayersVectorAll.push(...layerSearchOptions)

        // If layer is currently visible, add it to the active search layers as well
        if (layerConf?.selected) {
          const searchOptions = convertLayerConfToSearchOptions(layerConf)
          if (searchOptions?.[0]?.search?.group) {
            searchOptions[0].search.group = 'Active searchable layers'
          }

          searchLayersVectorActive.push(...searchOptions)
        }
      }
    })

    // Create groups of all options
    const options: ConfigLayerOptions[] = [
      ...searchLayersHardcoded,
      ...searchLayersKeyword,
      ...searchLayersVectorActive,
      ...searchLayersVectorAll
    ]

    // console.debug('SearchOptions - generated options', options)

    setSearchOptions(options)
  }

  if (!searchOptions || searchOptions.length === 0) {
    return null
  }

  // Set value to selected option or else the first option
  const selectedOption = searchOptions.find(option =>
    option.titleid === searchLayer?.titleid && option.search?.field === searchLayer?.search?.field) || searchOptions?.[0]

  const caption = getLayerCaption(selectedOption)
  const sanitisedCaption = sanitiseHtml(caption) || ''

  // The Advanced button should only be shown for vector layers.  Default search layers cannot have type GeoJSON, while vector layers will always be GeoJSON.
  // Layers configured with search fields should also be excluded from having the advanced button.  Search fields are designed for larger datasets that can't
  // efficiently be displayed as vector data.
  const showAdvancedButton = selectedOption?.type.toLowerCase() === 'geojson' && !searchLayer?.searchFields

  return (
    <FormControl component="fieldset" fullWidth className={classes.searchForm} >
      <SearchLayers
        searchOptions={searchOptions}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />

      <SearchAutoComplete />

      <Transition in={!!caption}>
        <Typography className={classes.caption}>
          <div dangerouslySetInnerHTML={{ __html: sanitisedCaption }} />
        </Typography>
      </Transition>

      {showAdvancedButton &&
        <SearchAdvancedButton />
      }

    </FormControl>
  )
}

export default SearchOptions
